import React, { useEffect, useState, useCallback } from 'react';
import {
  Layout, Typography, Card, Row, Col, Button, Table,
  Select, InputNumber, Input, Tag, message, Alert, Space, Popconfirm, Tooltip, Avatar, Divider, Modal
} from 'antd';
import {
  ReloadOutlined, SaveOutlined, SendOutlined, CalculatorOutlined,
  UserOutlined, CalendarOutlined, CheckCircleOutlined, TrophyOutlined,
  EditOutlined, DeleteOutlined, DollarOutlined, RiseOutlined, TableOutlined,
  CloseCircleOutlined, ClockCircleOutlined, AuditOutlined
} from '@ant-design/icons';
import Sidebar from './Sidebar';
import MainHeader from './MainHeader';
import api, { API_BASE_URL } from '../api';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const getFullImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

export default function PmsIncrementManagement() {
  const [collapsed, setCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submittingIncrement, setSubmittingIncrement] = useState(false);

  // Cycle & Appraisals list
  const [cycles, setCycles] = useState([]);
  const [selectedCycleId, setSelectedCycleId] = useState(null);
  const [appraisalsList, setAppraisalsList] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedAppraisal, setSelectedAppraisal] = useState(null);
  const [activeRatingScale, setActiveRatingScale] = useState(null);

  // Salary Template Components & Breakdown State (Section 20)
  const [salaryComponents, setSalaryComponents] = useState([]);
  const [componentBreakdown, setComponentBreakdown] = useState([]);

  // Increment Form States
  const [currentSalary, setCurrentSalary] = useState(40000);
  const [incrementType, setIncrementType] = useState('PERCENTAGE'); // 'PERCENTAGE' | 'FIXED'
  const [incrementValue, setIncrementValue] = useState(10);
  const [incrementEffectiveDate, setIncrementEffectiveDate] = useState('');
  const [incrementNotes, setIncrementNotes] = useState('');

  const fetchCycles = async () => {
    try {
      const res = await api.get('/admin/pms/cycles');
      if (res.data?.success) {
        const list = res.data.cycles || [];
        setCycles(list);
        if (list.length > 0) {
          const active = list.find((c) => c.active) || list[0];
          setSelectedCycleId(active.id);
        }
      }
    } catch (e) {
      console.error('Fetch cycles error:', e);
    }
  };

  useEffect(() => {
    fetchCycles();
  }, []);

  const buildBreakdown = useCallback((baseSal, type, val, comps) => {
    let rawComps = comps && comps.length > 0 ? comps : [
      { key: 'basic_salary', label: 'Basic Salary', value: Number((baseSal * 0.5).toFixed(2)) },
      { key: 'hra', label: 'HRA', value: Number((baseSal * 0.25).toFixed(2)) },
      { key: 'special_allowance', label: 'Special Allowance', value: Number((baseSal * 0.25).toFixed(2)) },
    ];

    const totalCompValue = rawComps.reduce((sum, c) => sum + Number(c.value || 0), 0);
    const incAmount = type === 'PERCENTAGE' ? (baseSal * val) / 100 : val;

    const list = rawComps.map((c) => {
      const existing = Number(c.value || 0) > 0 ? Number(c.value) : (totalCompValue > 0 ? 0 : Number((baseSal / rawComps.length).toFixed(2)));
      const ratio = totalCompValue > 0 ? existing / totalCompValue : 1 / rawComps.length;
      const compInc = Number((incAmount * ratio).toFixed(2));
      const revised = Number((existing + compInc).toFixed(2));
      return {
        key: c.key,
        label: c.label,
        existing,
        revisionDiff: compInc,
        revised,
      };
    });
    setComponentBreakdown(list);
  }, []);

  const loadData = useCallback(async () => {
    if (!selectedCycleId) return;
    try {
      setLoading(true);
      const params = { cycleId: selectedCycleId };
      if (selectedUserId) params.userId = selectedUserId;

      const res = await api.get('/admin/pms/manager-appraisals', { params });
      if (res.data?.success) {
        const list = res.data.appraisals || [];
        setAppraisalsList(list);
        setActiveRatingScale(res.data.activeRatingScale || null);

        if (selectedUserId && res.data.selectedAppraisal) {
          const appr = res.data.selectedAppraisal;
          const comps = res.data.salaryComponents || [];
          setSalaryComponents(comps);
          setSelectedAppraisal(appr);
          const baseSal = (appr.previousSalary && Number(appr.previousSalary) > 0)
            ? Number(appr.previousSalary)
            : (res.data.currentSalary || Number(appr.employee?.grossSalary) || 40000);
          setCurrentSalary(baseSal);
          const incT = appr.incrementType || 'PERCENTAGE';
          const incV = appr.incrementValue !== null && appr.incrementValue !== undefined ? Number(appr.incrementValue) : 10;
          setIncrementType(incT);
          setIncrementValue(incV);
          setIncrementEffectiveDate(appr.incrementEffectiveDate ? new Date(appr.incrementEffectiveDate).toISOString().split('T')[0] : '');
          setIncrementNotes(appr.incrementNotes || '');

          if (Array.isArray(appr.componentBreakdown) && appr.componentBreakdown.length > 0) {
            setComponentBreakdown(appr.componentBreakdown);
          } else {
            buildBreakdown(baseSal, incT, incV, comps);
          }
        } else if (!selectedUserId && list.length > 0) {
          setSelectedUserId(list[0].userId);
        }
      }
    } catch (e) {
      console.error('Fetch increment data error:', e);
      message.error('Failed to load increment data');
    } finally {
      setLoading(false);
    }
  }, [selectedCycleId, selectedUserId, buildBreakdown]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Recalculate breakdown when inputs change
  useEffect(() => {
    if (selectedAppraisal) {
      buildBreakdown(currentSalary, incrementType, Number(incrementValue || 0), salaryComponents);
    }
  }, [currentSalary, incrementType, incrementValue, salaryComponents, selectedAppraisal, buildBreakdown]);

  const handleComponentRevisedChange = (index, newRevised) => {
    const updated = [...componentBreakdown];
    const item = updated[index];
    const rev = Number(newRevised || 0);
    const diff = Number((rev - item.existing).toFixed(2));
    updated[index] = {
      ...item,
      revised: rev,
      revisionDiff: diff,
    };
    setComponentBreakdown(updated);
  };

  const handleSaveIncrement = async (applyToPayroll = false) => {
    if (!selectedAppraisal) {
      return message.warning('Please select an employee appraisal to process increment.');
    }
    if (incrementValue === null || incrementValue === undefined || incrementValue < 0) {
      return message.warning('Please enter a valid increment value.');
    }

    try {
      setSubmittingIncrement(true);
      const payload = {
        incrementType,
        incrementValue: Number(incrementValue),
        effectiveDate: incrementEffectiveDate,
        notes: incrementNotes,
        applyToPayroll,
        componentBreakdown,
      };

      const res = await api.post(`/admin/pms/manager-appraisals/${selectedAppraisal.id}/increment`, payload);
      if (res.data?.success) {
        message.success(res.data.message || 'Increment & component breakdown saved successfully!');
        loadData();
      } else {
        message.error(res.data?.message || 'Failed saving increment');
      }
    } catch (e) {
      console.error('Save increment error:', e);
      message.error(e.response?.data?.message || 'Failed saving increment');
    } finally {
      setSubmittingIncrement(false);
    }
  };

  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectingAppraisalId, setRejectingAppraisalId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [submittingAction, setSubmittingAction] = useState(false);

  const handleDeleteIncrement = async (appraisalId) => {
    try {
      setLoading(true);
      const res = await api.delete(`/admin/pms/manager-appraisals/${appraisalId}/increment`);
      if (res.data?.success) {
        message.success(res.data.message || 'Increment record reset successfully!');
        loadData();
      } else {
        message.error(res.data?.message || 'Failed deleting increment');
      }
    } catch (e) {
      console.error('Delete increment error:', e);
      message.error(e.response?.data?.message || 'Failed deleting increment');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveRevisionByAppraisal = async (appraisalId) => {
    try {
      setSubmittingAction(true);
      // Fetch revision record or call direct apply on manager-appraisals
      const targetAppr = appraisalsList.find(a => a.id === appraisalId);
      if (!targetAppr) return;

      const payload = {
        incrementType: targetAppr.incrementType || 'PERCENTAGE',
        incrementValue: Number(targetAppr.incrementValue || 10),
        effectiveDate: targetAppr.incrementEffectiveDate,
        notes: targetAppr.incrementNotes,
        applyToPayroll: true,
        componentBreakdown: targetAppr.componentBreakdown,
      };

      const res = await api.post(`/admin/pms/manager-appraisals/${appraisalId}/increment`, payload);
      if (res.data?.success) {
        message.success(res.data.message || 'HR Approved & Synced to Payroll!');
        loadData();
      } else {
        message.error(res.data?.message || 'Failed approving revision');
      }
    } catch (e) {
      console.error('Approve revision error:', e);
      message.error(e.response?.data?.message || 'Failed approving revision');
    } finally {
      setSubmittingAction(false);
    }
  };

  const handleConfirmReject = async () => {
    if (!rejectingAppraisalId) return;
    try {
      setSubmittingAction(true);
      // Revert/Reset with notes or deletion
      const res = await api.delete(`/admin/pms/manager-appraisals/${rejectingAppraisalId}/increment`);
      if (res.data?.success) {
        message.success('Salary revision rejected by HR.');
        setRejectModalVisible(false);
        setRejectingAppraisalId(null);
        setRejectionReason('');
        loadData();
      } else {
        message.error(res.data?.message || 'Failed rejecting revision');
      }
    } catch (e) {
      console.error('Reject revision error:', e);
      message.error('Failed rejecting revision');
    } finally {
      setSubmittingAction(false);
    }
  };

  const calculatedIncrementAmount = componentBreakdown.length > 0
    ? componentBreakdown.reduce((sum, item) => sum + Number(item.revisionDiff || 0), 0)
    : (incrementType === 'PERCENTAGE' ? ((currentSalary || 40000) * (incrementValue || 0)) / 100 : (incrementValue || 0));

  const proposedNewSalary = componentBreakdown.length > 0
    ? componentBreakdown.reduce((sum, item) => sum + Number(item.revised || 0), 0)
    : (currentSalary || 40000) + calculatedIncrementAmount;

  const currentScore = selectedAppraisal
    ? (selectedAppraisal.overrideRating !== null && selectedAppraisal.overrideRating !== undefined
        ? selectedAppraisal.overrideRating
        : (selectedAppraisal.overallManagerRating || selectedAppraisal.overallSelfRating || 0))
    : 0;

  const maxPts = activeRatingScale?.maxPoints ? Number(activeRatingScale.maxPoints) : 5;

  // History table columns
  const tableColumns = [
    {
      title: 'Employee',
      dataIndex: 'employee',
      key: 'emp',
      render: (_, r) => {
        const name = r.employee?.profile?.name || r.employee?.username || `User #${r.userId}`;
        const desig = r.employee?.profile?.designation ? ` (${r.employee.profile.designation})` : '';
        const photo = getFullImageUrl(r.employee?.profile?.photoUrl);
        return (
          <Space>
            {photo ? (
              <Avatar src={photo} />
            ) : (
              <Avatar style={{ backgroundColor: '#1890ff' }}>
                {name.charAt(0).toUpperCase()}
              </Avatar>
            )}
            <div>
              <Text strong style={{ color: '#0f172a', display: 'block' }}>{name}</Text>
              <Text type="secondary" style={{ fontSize: '11px' }}>{desig || 'Staff Member'}</Text>
            </div>
          </Space>
        );
      },
    },
    {
      title: 'Appraisal Rating',
      dataIndex: 'overallManagerRating',
      key: 'rating',
      render: (_, r) => {
        const score = r.overrideRating !== null ? r.overrideRating : (r.overallManagerRating || r.overallSelfRating || 0);
        return <Tag color="gold" style={{ fontWeight: 'bold' }}><TrophyOutlined style={{ marginRight: 4 }} />{score} / {maxPts}</Tag>;
      },
    },
    {
      title: 'Type',
      dataIndex: 'incrementType',
      key: 'type',
      render: (v) => <Tag color={v === 'FIXED' ? 'purple' : 'cyan'}>{v || 'PERCENTAGE'}</Tag>,
    },
    {
      title: 'Base Salary',
      dataIndex: 'previousSalary',
      key: 'prevSal',
      render: (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`,
    },
    {
      title: 'Increment',
      dataIndex: 'incrementAmount',
      key: 'amt',
      render: (v) => <Text style={{ color: '#16a34a', fontWeight: 'bold' }}>+₹{Number(v || 0).toLocaleString('en-IN')}</Text>,
    },
    {
      title: 'Revised Salary',
      dataIndex: 'newSalary',
      key: 'newSal',
      render: (v) => <Text style={{ color: '#2563eb', fontWeight: 'bold', fontSize: '14px' }}>₹{Number(v || 0).toLocaleString('en-IN')}</Text>,
    },
    {
      title: 'HR Approval & Payroll Status',
      dataIndex: 'incrementStatus',
      key: 'status',
      render: (s) => {
        if (s === 'APPLIED') {
          return <Tag color="success" style={{ fontWeight: 'bold' }}><CheckCircleOutlined /> Synced in Payroll</Tag>;
        }
        if (s === 'PENDING_APPROVAL') {
          return <Tag color="warning" style={{ fontWeight: 'bold' }}><ClockCircleOutlined /> Pending HR Approval</Tag>;
        }
        if (s === 'APPROVED') {
          return <Tag color="processing" style={{ fontWeight: 'bold' }}><AuditOutlined /> HR Approved</Tag>;
        }
        if (s === 'REJECTED') {
          return <Tag color="error" style={{ fontWeight: 'bold' }}><CloseCircleOutlined /> Rejected by HR</Tag>;
        }
        return <Tag color="default">Draft</Tag>;
      },
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, r) => (
        <Space size="small">
          <Tooltip title="Edit Revision">
            <Button
              type="primary"
              ghost
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setSelectedUserId(r.userId);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              Edit
            </Button>
          </Tooltip>

          {r.incrementStatus !== 'APPLIED' && (
            <Tooltip title="HR Direct Approve & Sync">
              <Button
                type="primary"
                size="small"
                style={{ background: '#16a34a', borderColor: '#16a34a' }}
                icon={<CheckCircleOutlined />}
                loading={submittingAction}
                onClick={() => handleApproveRevisionByAppraisal(r.id)}
              >
                Approve
              </Button>
            </Tooltip>
          )}

          {r.incrementStatus === 'PENDING_APPROVAL' && (
            <Tooltip title="HR Reject Revision">
              <Button
                type="default"
                danger
                size="small"
                icon={<CloseCircleOutlined />}
                onClick={() => {
                  setRejectingAppraisalId(r.id);
                  setRejectModalVisible(true);
                }}
              >
                Reject
              </Button>
            </Tooltip>
          )}

          <Tooltip title="Reset Revision">
            <Popconfirm
              title="Reset Salary Revision?"
              description="This will reset the increment record for this employee."
              onConfirm={() => handleDeleteIncrement(r.id)}
              okText="Reset"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
              >
                Reset
              </Button>
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      <Layout style={{ marginLeft: collapsed ? 80 : 200, minHeight: '100vh', transition: 'margin-left 0.2s', background: '#f8fafc' }}>
        <MainHeader collapsed={collapsed} setCollapsed={setCollapsed} title="Increment Management" />
        <Content style={{ margin: '24px 24px', overflow: 'initial' }}>
          
          {/* Subtitle & Refresh Bar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <Text type="secondary" style={{ fontSize: '13px' }}>
                Review appraisal performance ratings and manage post-appraisal salary revisions with instant Payroll sync.
              </Text>
            </div>
            <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading} size="small">
              Refresh
            </Button>
          </div>

          {/* Clean Filter Bar */}
          <Card
            bordered={false}
            style={{ marginBottom: 20, borderRadius: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
            bodyStyle={{ padding: '16px 20px' }}
          >
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={12} md={8}>
                <Text strong style={{ color: '#475569', fontSize: '12px' }}>
                  <CalendarOutlined style={{ marginRight: 6 }} /> APPRAISAL CYCLE
                </Text>
                <Select
                  style={{ width: '100%', marginTop: 4 }}
                  placeholder="Select Appraisal Cycle"
                  value={selectedCycleId}
                  onChange={(val) => {
                    setSelectedCycleId(val);
                    setSelectedUserId(null);
                  }}
                >
                  {cycles.map((c) => (
                    <Option key={c.id} value={c.id}>
                      {c.cycleName} ({c.financialYear}) {c.active ? ' [Active]' : ''}
                    </Option>
                  ))}
                </Select>
              </Col>

              <Col xs={24} sm={12} md={12}>
                <Text strong style={{ color: '#475569', fontSize: '12px' }}>
                  <UserOutlined style={{ marginRight: 6 }} /> SELECT EMPLOYEE
                </Text>
                <Select
                  showSearch
                  style={{ width: '100%', marginTop: 4 }}
                  placeholder="Select Employee to Process Increment"
                  value={selectedUserId}
                  onChange={(val) => setSelectedUserId(val)}
                  optionFilterProp="children"
                >
                  {appraisalsList.map((appr) => {
                    const name = appr.employee?.profile?.name || appr.employee?.username || `User #${appr.userId}`;
                    const desig = appr.employee?.profile?.designation ? ` (${appr.employee.profile.designation})` : '';
                    const statusTag = appr.incrementStatus === 'APPLIED' ? ' ✓ Synced' : '';
                    return (
                      <Option key={appr.userId} value={appr.userId}>
                        {name} {desig} {statusTag}
                      </Option>
                    );
                  })}
                </Select>
              </Col>
            </Row>
          </Card>

          {selectedAppraisal ? (
            <div>
              {/* Executive Workspace Card */}
              <Card
                bordered={false}
                style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', marginBottom: 24, background: '#ffffff' }}
                bodyStyle={{ padding: '24px' }}
              >
                {/* Header Row: Employee & Appraisal Status */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, paddingBottom: 16, borderBottom: '1px solid #f1f5f9', marginBottom: 20 }}>
                  {(() => {
                    const empPhoto = getFullImageUrl(selectedAppraisal.employee?.profile?.photoUrl);
                    const empName = selectedAppraisal.employee?.profile?.name || selectedAppraisal.employee?.username || `User #${selectedAppraisal.userId}`;
                    return (
                      <Space size={12}>
                        {empPhoto ? (
                          <Avatar size={48} src={empPhoto} />
                        ) : (
                          <Avatar size={48} style={{ backgroundColor: '#2563eb', fontWeight: 'bold' }}>
                            {empName.charAt(0).toUpperCase()}
                          </Avatar>
                        )}
                        <div>
                          <Title level={4} style={{ margin: 0, color: '#0f172a' }}>
                            {empName}
                          </Title>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {selectedAppraisal.employee?.profile?.designation || 'Staff Member'} • Recommendation: <Text strong style={{ color: '#16a34a' }}>{selectedAppraisal.recommendation || 'Increment'}</Text>
                          </Text>
                        </div>
                      </Space>
                    );
                  })()}

                  <Space size={12}>
                    <Tag color="gold" style={{ fontSize: '14px', padding: '4px 12px', fontWeight: 'bold', borderRadius: 6 }}>
                      <TrophyOutlined style={{ marginRight: 6 }} /> Score: {currentScore} / {maxPts}
                    </Tag>
                    {selectedAppraisal.incrementStatus === 'APPLIED' ? (
                      <Tag color="success" style={{ fontSize: '13px', padding: '4px 12px', fontWeight: 'bold', borderRadius: 6 }}>
                        <CheckCircleOutlined style={{ marginRight: 6 }} /> Synced to Payroll
                      </Tag>
                    ) : (
                      <Tag color="warning" style={{ fontSize: '13px', padding: '4px 12px', borderRadius: 6 }}>
                        Pending Payroll Action
                      </Tag>
                    )}
                  </Space>
                </div>

                {/* Form Inputs Grid */}
                <Row gutter={[20, 16]} style={{ marginBottom: 20 }}>
                  <Col xs={24} sm={12} md={6}>
                    <Text strong style={{ color: '#334155', fontSize: '13px' }}>Increment Type</Text>
                    <Select
                      style={{ width: '100%', marginTop: 6 }}
                      value={incrementType}
                      onChange={(val) => setIncrementType(val)}
                      size="large"
                    >
                      <Option value="PERCENTAGE">Percentage (%)</Option>
                      <Option value="FIXED">Fixed Amount (₹)</Option>
                    </Select>
                  </Col>

                  <Col xs={24} sm={12} md={6}>
                    <Text strong style={{ color: '#334155', fontSize: '13px' }}>
                      {incrementType === 'PERCENTAGE' ? 'Percentage Rate (%)' : 'Fixed Revision Amount (₹)'}
                    </Text>
                    <InputNumber
                      min={0}
                      size="large"
                      step={incrementType === 'PERCENTAGE' ? 0.5 : 500}
                      style={{ width: '100%', marginTop: 6 }}
                      value={incrementValue}
                      onChange={(val) => setIncrementValue(val || 0)}
                      addonAfter={incrementType === 'PERCENTAGE' ? '%' : '₹'}
                    />
                  </Col>

                  <Col xs={24} sm={12} md={6}>
                    <Text strong style={{ color: '#334155', fontSize: '13px' }}>Effective Date</Text>
                    <Input
                      type="date"
                      size="large"
                      style={{ width: '100%', marginTop: 6 }}
                      value={incrementEffectiveDate || ''}
                      onChange={(e) => setIncrementEffectiveDate(e.target.value)}
                    />
                  </Col>

                  <Col xs={24} sm={12} md={6}>
                    <Text strong style={{ color: '#334155', fontSize: '13px' }}>Justification / Notes</Text>
                    <Input
                      size="large"
                      style={{ width: '100%', marginTop: 6 }}
                      value={incrementNotes}
                      onChange={(e) => setIncrementNotes(e.target.value)}
                      placeholder="e.g. Q3 Merit Revision"
                    />
                  </Col>
                </Row>

                {/* Sleek Impact Metrics Cards Grid */}
                <div style={{
                  background: '#f8fafc',
                  borderRadius: 10,
                  padding: '18px 24px',
                  marginBottom: 24,
                  border: '1px solid #e2e8f0',
                }}>
                  <Row gutter={[24, 16]} align="middle">
                    <Col xs={24} sm={8}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 40, height: 40, borderRadius: 8, background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <DollarOutlined style={{ fontSize: 20, color: '#475569' }} />
                        </div>
                        <div>
                          <Text type="secondary" style={{ fontSize: '12px' }}>Current Gross Salary</Text>
                          <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1e293b' }}>
                            ₹{(currentSalary || 40000).toLocaleString('en-IN')}
                          </div>
                        </div>
                      </div>
                    </Col>

                    <Col xs={24} sm={8}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 40, height: 40, borderRadius: 8, background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <RiseOutlined style={{ fontSize: 20, color: '#16a34a' }} />
                        </div>
                        <div>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            Revised Increment ({incrementType === 'PERCENTAGE' ? `${incrementValue}%` : 'Fixed'})
                          </Text>
                          <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#16a34a' }}>
                            +₹{calculatedIncrementAmount.toLocaleString('en-IN')}
                          </div>
                        </div>
                      </div>
                    </Col>

                    <Col xs={24} sm={8}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 40, height: 40, borderRadius: 8, background: '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <CalculatorOutlined style={{ fontSize: 20, color: '#4f46e5' }} />
                        </div>
                        <div>
                          <Text type="secondary" style={{ fontSize: '12px' }}>New Proposed Salary</Text>
                          <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#4f46e5' }}>
                            ₹{proposedNewSalary.toLocaleString('en-IN')}
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* Section 20: Salary Template Component Revision Breakdown Table */}
                <Card
                  type="inner"
                  style={{
                    borderRadius: 10,
                    borderColor: '#cbd5e1',
                    marginBottom: 24,
                    background: '#ffffff',
                  }}
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                      <span style={{ color: '#1e293b', fontWeight: 'bold', fontSize: '15px' }}>
                        <TableOutlined style={{ marginRight: 8, color: '#2563eb' }} /> Section 20: Salary Template Component Revision Breakdown
                      </span>
                      <Tag color="blue" style={{ fontWeight: 'bold' }}>Assigned Template Components</Tag>
                    </div>
                  }
                >
                  <Paragraph style={{ fontSize: '12px', color: '#64748b', marginBottom: 16 }}>
                    The breakdown below reads the exact salary components from the employee's assigned Salary Template (e.g. Basic, HRA, Special Allowance). You can fine-tune revised component amounts before syncing to Payroll.
                  </Paragraph>

                  <Table
                    dataSource={componentBreakdown}
                    rowKey="key"
                    pagination={false}
                    size="middle"
                    bordered
                    columns={[
                      {
                        title: 'Salary Component (Template)',
                        dataIndex: 'label',
                        key: 'label',
                        render: (text) => <Text strong style={{ color: '#1e293b' }}>{text}</Text>,
                      },
                      {
                        title: 'Existing Amount (₹)',
                        dataIndex: 'existing',
                        key: 'existing',
                        render: (val) => `₹${Number(val || 0).toLocaleString('en-IN')}`,
                      },
                      {
                        title: 'Revised Amount (₹)',
                        dataIndex: 'revised',
                        key: 'revised',
                        render: (val, record, idx) => (
                          <InputNumber
                            min={0}
                            step={100}
                            style={{ width: 150 }}
                            value={val}
                            onChange={(newVal) => handleComponentRevisedChange(idx, newVal)}
                            formatter={(v) => `₹ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={(v) => v.replace(/\₹\s?|(,*)/g, '')}
                          />
                        ),
                      },
                      {
                        title: 'Revision Difference (+₹)',
                        dataIndex: 'revisionDiff',
                        key: 'diff',
                        render: (val) => (
                          <Text style={{ color: val >= 0 ? '#16a34a' : '#dc2626', fontWeight: 'bold' }}>
                            {val >= 0 ? `+₹${Number(val).toLocaleString('en-IN')}` : `-₹${Math.abs(val).toLocaleString('en-IN')}`}
                          </Text>
                        ),
                      },
                    ]}
                    summary={() => {
                      const totalExisting = componentBreakdown.reduce((sum, item) => sum + Number(item.existing || 0), 0);
                      const totalRevised = componentBreakdown.reduce((sum, item) => sum + Number(item.revised || 0), 0);
                      const totalDiff = totalRevised - totalExisting;
                      return (
                        <Table.Summary fixed>
                          <Table.Summary.Row style={{ background: '#f8fafc', fontWeight: 'bold' }}>
                            <Table.Summary.Cell index={0}>
                              <Text strong style={{ fontSize: '14px', color: '#0f172a' }}>Gross Total Salary</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={1}>
                              <Text strong style={{ fontSize: '14px' }}>₹{totalExisting.toLocaleString('en-IN')}</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={2}>
                              <Text strong style={{ fontSize: '15px', color: '#2563eb' }}>₹{totalRevised.toLocaleString('en-IN')}</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={3}>
                              <Text strong style={{ fontSize: '15px', color: '#16a34a' }}>+₹{totalDiff.toLocaleString('en-IN')}</Text>
                            </Table.Summary.Cell>
                          </Table.Summary.Row>
                        </Table.Summary>
                      );
                    }}
                  />
                </Card>

                {/* Primary Action Buttons Bar */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                  <Button
                    size="large"
                    icon={<SaveOutlined />}
                    loading={submittingIncrement}
                    onClick={() => handleSaveIncrement(false)}
                  >
                    Propose Revision (Pending HR)
                  </Button>
                  <Button
                    type="primary"
                    size="large"
                    icon={<SendOutlined />}
                    style={{ background: '#16a34a', borderColor: '#16a34a', fontWeight: 'bold' }}
                    loading={submittingIncrement}
                    onClick={() => handleSaveIncrement(true)}
                  >
                    HR Approve & Sync to Payroll
                  </Button>
                </div>
              </Card>

              {/* Increments History Log */}
              <Card
                bordered={false}
                style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}
                bodyStyle={{ padding: '20px 24px' }}
                title={<Text strong style={{ fontSize: '15px', color: '#0f172a' }}>Cycle Salary Revisions Log (Section 21 Audit Trail)</Text>}
              >
                <Table
                  dataSource={appraisalsList}
                  columns={tableColumns}
                  rowKey="id"
                  pagination={false}
                  size="middle"
                />
              </Card>
            </div>
          ) : (
            <Alert message="Please select an appraisal cycle and employee from the filter bar above." type="info" showIcon />
          )}

          {/* Section 21: Rejection Reason Modal */}
          <Modal
            title="Reject Salary Revision"
            open={rejectModalVisible}
            onOk={handleConfirmReject}
            onCancel={() => setRejectModalVisible(false)}
            okText="Confirm Rejection"
            okButtonProps={{ danger: true, loading: submittingAction }}
          >
            <Paragraph style={{ fontSize: '13px', color: '#475569' }}>
              Please provide a reason for rejecting this salary revision proposal. This will be logged in the audit history.
            </Paragraph>
            <Input.TextArea
              rows={3}
              placeholder="e.g. Revision exceeds budget limit or requires manager re-review..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            />
          </Modal>

        </Content>
      </Layout>
    </Layout>
  );
}
